import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, MapPin, Clock, PoundSterling, Music, Globe, Search, Navigation, Loader2, Plus, Ticket, ExternalLink, Tag, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import PageHeader from '../components/PageHeader';
import EmptyState from '../components/EmptyState';
import { useAuth } from '../context/AuthContext';
import { getEventImage } from '../utils/eventImages';
import TicketPurchaseModal from '../components/tickets/TicketPurchaseModal';

import { API_URL } from '../utils/api';

// Get current ticket tier based on today's date
const getCurrentTier = (ticketTiers) => {
  if (!ticketTiers || ticketTiers.length === 0) return null;
  
  const now = new Date();
  
  // Sort tiers by start_date
  const sortedTiers = [...ticketTiers].sort((a, b) => 
    new Date(a.start_date) - new Date(b.start_date)
  );
  
  // Find the currently active tier
  for (const tier of sortedTiers) {
    const startDate = tier.start_date ? new Date(tier.start_date) : new Date(0);
    const endDate = tier.end_date ? new Date(tier.end_date) : new Date('2099-12-31');
    
    // Set end date to end of day
    endDate.setHours(23, 59, 59, 999);
    
    if (now >= startDate && now <= endDate) {
      // Calculate days until this tier ends
      const daysRemaining = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
      return {
        ...tier,
        daysRemaining,
        isExpiringSoon: daysRemaining <= 7
      };
    }
  }
  
  // If no active tier, return the first upcoming tier or the last tier
  const futureTiers = sortedTiers.filter(t => new Date(t.start_date) > now);
  if (futureTiers.length > 0) {
    return { ...futureTiers[0], notYetActive: true };
  }
  
  // Return the last tier if all have passed
  return sortedTiers[sortedTiers.length - 1];
};

// Generate SEO-friendly event URL
const getEventUrl = (event) => {
  if (event.full_slug) {
    return `/events/${event.full_slug}`;
  } else if (event.host_slug && event.slug) {
    return `/events/${event.host_slug}/${event.slug}`;
  } else if (event.slug) {
    return `/events/${event.slug}`;
  }
  return `/events/${event.event_id}`;
};

export default function EventsPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  
  // Ticket purchase modal state
  const [ticketModalOpen, setTicketModalOpen] = useState(false);
  const [selectedTicketEvent, setSelectedTicketEvent] = useState(null);
  
  // Featured events carousel state
  const [carouselIndex, setCarouselIndex] = useState(0);
  const carouselIntervalRef = useRef(null);
  
  // Distance filter state
  const [locationInput, setLocationInput] = useState('');
  const [distance, setDistance] = useState(searchParams.get('distance') || 'nationwide');
  const [userCoords, setUserCoords] = useState(
    searchParams.get('lat') && searchParams.get('lng') 
      ? { lat: parseFloat(searchParams.get('lat')), lng: parseFloat(searchParams.get('lng')) }
      : null
  );
  const [gettingLocation, setGettingLocation] = useState(false);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      const search = searchParams.get('search');
      if (search) params.append('search', search);
      
      const lat = searchParams.get('lat');
      const lng = searchParams.get('lng');
      const dist = searchParams.get('distance');
      if (lat && lng && dist && dist !== 'nationwide') {
        params.append('lat', lat);
        params.append('lng', lng);
        params.append('distance', dist);
      }

      const response = await fetch(`${API_URL}/api/events/all?${params.toString()}`);
      if (response.ok) {
        const data = await response.json();
        setEvents(data);
      }
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  }, [searchParams]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  useEffect(() => {
    if (searchParams.get('lat') && searchParams.get('lng')) {
      setLocationInput('Using your location');
    }
  }, [searchParams]);

  // Auto-rotate featured events carousel - shifts one event at a time
  const featuredEvents = events.filter(e => e.is_featured);
  const itemsToShow = 3;
  // Total positions = number of events minus (itemsToShow - 1), minimum 1
  const totalPositions = Math.max(1, featuredEvents.length - itemsToShow + 1);
  
  useEffect(() => {
    if (featuredEvents.length > itemsToShow) {
      carouselIntervalRef.current = setInterval(() => {
        setCarouselIndex(prev => (prev + 1) % totalPositions);
      }, 5000); // Rotate every 5 seconds
      
      return () => {
        if (carouselIntervalRef.current) {
          clearInterval(carouselIntervalRef.current);
        }
      };
    }
  }, [featuredEvents.length, totalPositions]);

  // Reset carousel index when events change
  useEffect(() => {
    setCarouselIndex(0);
  }, [events]);

  const goToCarouselPage = (index) => {
    setCarouselIndex(index);
    // Reset auto-rotate timer
    if (carouselIntervalRef.current) {
      clearInterval(carouselIntervalRef.current);
      if (featuredEvents.length > itemsToShow) {
        carouselIntervalRef.current = setInterval(() => {
          setCarouselIndex(prev => (prev + 1) % totalPositions);
        }, 5000);
      }
    }
  };

  const goToPrevCarousel = () => goToCarouselPage((carouselIndex - 1 + totalPositions) % totalPositions);
  const goToNextCarousel = () => goToCarouselPage((carouselIndex + 1) % totalPositions);

  // Show 3 events starting from current index
  const visibleFeaturedEvents = featuredEvents.slice(
    carouselIndex,
    carouselIndex + itemsToShow
  );

  const getUserLocation = () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser');
      return;
    }
    
    setGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserCoords({
          lat: position.coords.latitude,
          lng: position.coords.longitude
        });
        setLocationInput('Using your location');
        setGettingLocation(false);
      },
      (error) => {
        console.error('Geolocation error:', error);
        alert('Unable to get your location. Please enter a postcode or town.');
        setGettingLocation(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const geocodeLocation = async (location) => {
    try {
      const response = await fetch(`${API_URL}/api/geocode?address=${encodeURIComponent(location)}`);
      if (response.ok) {
        const data = await response.json();
        return { lat: data.latitude, lng: data.longitude };
      }
    } catch (error) {
      console.error('Geocoding error:', error);
    }
    return null;
  };

  const handleLocationInputChange = (e) => {
    setLocationInput(e.target.value);
    if (userCoords && e.target.value !== 'Using your location') {
      setUserCoords(null);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    
    if (searchQuery) {
      params.set('search', searchQuery);
    }
    
    if (distance !== 'nationwide') {
      let coords = userCoords;
      
      if (locationInput && locationInput !== 'Using your location' && !userCoords) {
        coords = await geocodeLocation(locationInput);
        if (coords) setUserCoords(coords);
      }
      
      if (coords) {
        params.set('lat', coords.lat);
        params.set('lng', coords.lng);
        params.set('distance', distance);
      }
    }
    
    setSearchParams(params);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setLocationInput('');
    setDistance('nationwide');
    setUserCoords(null);
    setSearchParams({});
  };

  return (
    <div className="min-h-screen bg-[var(--bg-base)]" data-testid="events-page">
      {/* Header */}
      <PageHeader
        title="Upcoming Events"
        subtitle="Find line dance events near you"
        actions={
          <Link
            to={user ? "/dashboard" : "/register"}
            className="inline-flex items-center px-4 py-2 bg-violet-600 hover:bg-violet-500 text-white font-semibold rounded-full transition-colors text-sm"
            data-testid="add-event-btn"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add Your Event
          </Link>
        }
      />
      {/* Search & Filter toolbar */}
      <div className="bg-white border-b border-stone-200">
        <div className="container mx-auto px-6 lg:px-12 py-4">
          <form onSubmit={handleSearch} className="max-w-4xl mx-auto">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400 w-5 h-5" />
                  <Input
                    type="text"
                    placeholder="Search events..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-12 h-12 rounded-xl bg-white border border-stone-200"
                    data-testid="events-search-input"
                  />
                </div>
                
                <div className="relative flex-1">
                  <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400 w-5 h-5" />
                  <Input
                    type="text"
                    placeholder="Postcode or town..."
                    value={locationInput}
                    onChange={handleLocationInputChange}
                    className="pl-12 pr-12 h-12 rounded-xl bg-white border border-stone-200"
                    data-testid="events-location-input"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={getUserLocation}
                    disabled={gettingLocation}
                    aria-label="Use my location"
                    title="Use my location"
                    className="absolute right-3 top-1/2 -translate-y-1/2 h-8 w-8 text-violet-600 hover:text-violet-600 hover:bg-violet-50"
                  >
                    {gettingLocation ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Navigation className="w-5 h-5" />
                    )}
                  </Button>
                </div>
                
                <Select value={distance} onValueChange={setDistance}>
                  <SelectTrigger className="w-full sm:w-40 h-12 rounded-xl bg-white border border-stone-200" data-testid="events-distance-select">
                    <SelectValue placeholder="Distance" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">Within 5 miles</SelectItem>
                    <SelectItem value="10">Within 10 miles</SelectItem>
                    <SelectItem value="25">Within 25 miles</SelectItem>
                    <SelectItem value="50">Within 50 miles</SelectItem>
                    <SelectItem value="nationwide">Nationwide</SelectItem>
                  </SelectContent>
                </Select>
                
                <Button type="submit" className="h-12 px-6 bg-violet-600 hover:bg-violet-500 rounded-xl" data-testid="events-search-btn">
                  Search
                </Button>
              </div>
            </form>
          </div>
        </div>

      {/* Events Grid */}
      <div className="container mx-auto px-6 lg:px-12 py-12">
        {/* Results count and clear filters */}
        <div className="flex items-center justify-between mb-6">
          <p className="text-slate-500">
            {events.length} event{events.length !== 1 ? 's' : ''} found
            {distance !== 'nationwide' && userCoords && ` within ${distance} miles`}
          </p>
          {(searchQuery || distance !== 'nationwide' || locationInput) && (
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              Clear filters
            </Button>
          )}
        </div>
        
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white rounded-2xl overflow-hidden">
                <div className="h-48 bg-slate-200 animate-pulse" />
                <div className="p-5 space-y-3">
                  <div className="h-6 bg-slate-200 rounded animate-pulse w-3/4" />
                  <div className="h-4 bg-slate-200 rounded animate-pulse w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : events.length === 0 ? (
          <EmptyState
            icon={Calendar}
            title="No upcoming events"
            description="Check back soon for new dance events!"
            action={
              <Link to="/clubs">
                <Button className="bg-violet-600 hover:bg-violet-500">
                  Browse Clubs
                </Button>
              </Link>
            }
          />
        ) : (
          <>
            {/* Featured Events Carousel - shown at top in a box */}
            {featuredEvents.length > 0 && (
              <div className="mb-10 bg-gradient-to-br from-violet-50 to-purple-50 rounded-2xl p-6 border border-violet-100">
                <div className="flex items-center justify-between mb-5">
                  <h2 className="text-xl font-bold text-slate-800">Recommended Events</h2>
                  {totalPositions > 1 && (
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={goToPrevCarousel}
                        className="h-8 w-8 p-0 rounded-full bg-white hover:bg-violet-100"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <div className="flex gap-1.5">
                        {Array.from({ length: totalPositions }).map((_, idx) => (
                          <button
                            key={idx}
                            onClick={() => goToCarouselPage(idx)}
                            className={`w-2 h-2 rounded-full transition-all ${
                              idx === carouselIndex 
                                ? 'bg-violet-500 w-4' 
                                : 'bg-violet-200 hover:bg-violet-300'
                            }`}
                          />
                        ))}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={goToNextCarousel}
                        className="h-8 w-8 p-0 rounded-full bg-white hover:bg-violet-100"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
                
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {visibleFeaturedEvents.map((event, index) => (
                        <motion.div
                          key={event.event_id}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ duration: 0.5, ease: "easeInOut" }}
                          layout
                        >
                          <div className="bg-white rounded-2xl overflow-hidden border border-violet-200 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 h-full group">
                            <div className="relative h-48 overflow-hidden">
                              <Link to={getEventUrl(event)} data-testid={`featured-event-card-${event.event_id}`}>
                                <img
                                  src={getEventImage(event.event_id, event.image_url)}
                                  alt={event.title}
                                  className="w-full h-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
                                />
                                <div className="absolute top-3 left-3 bg-violet-600 text-white font-bold px-4 py-2 rounded-lg text-center shadow-lg">
                                  <div className="text-2xl leading-none">{new Date(event.event_date).getDate()}</div>
                                  <div className="text-xs uppercase">{new Date(event.event_date).toLocaleDateString('en-GB', { month: 'short' })}</div>
                                </div>
                                {event.type === 'community' && (
                                  <Badge className="absolute top-3 right-3 bg-blue-500 text-white">
                                    <Globe className="w-3 h-3 mr-1" />
                                    Community
                                  </Badge>
                                )}
                              </Link>
                              {/* Ticket buttons overlay */}
                              {event.ticket_widget_code && (
                                <Link
                                  to={`${getEventUrl(event)}#tickets`}
                                  className="absolute bottom-0 left-0 right-0 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white px-4 py-2 flex items-center justify-center gap-2 transition-all"
                                >
                                  <Ticket className="w-4 h-4" />
                                  <span className="text-sm font-semibold">Get Tickets</span>
                                </Link>
                              )}
                              {!event.ticket_widget_code && event.external_ticket_url && (
                                <a
                                  href={event.external_ticket_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  className="absolute bottom-0 left-0 right-0 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white px-4 py-2 flex items-center justify-center gap-2 transition-all"
                                >
                                  <Ticket className="w-4 h-4" />
                                  <span className="text-sm font-semibold">Get Tickets</span>
                                </a>
                              )}
                              {!event.ticket_widget_code && !event.external_ticket_url && event.ticketing_enabled && (
                                <button
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setSelectedTicketEvent(event);
                                    setTicketModalOpen(true);
                                  }}
                                  className="absolute bottom-0 left-0 right-0 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white px-4 py-2 flex items-center justify-center gap-2 transition-all"
                                >
                                  <Ticket className="w-4 h-4" />
                                  <span className="text-sm font-semibold">Buy Tickets Here</span>
                                </button>
                              )}
                            </div>
                            <Link to={getEventUrl(event)}>
                              <div className="p-5">
                                <h3 className="text-lg font-bold text-slate-900 mb-2 line-clamp-1 group-hover:text-violet-600 transition-colors">{event.title}</h3>
                                <p className="text-slate-600 text-sm line-clamp-2 mb-4">{event.description}</p>
                                
                                <div className="space-y-2 text-sm">
                                  {event.type === 'club' ? (
                                    <div className="flex items-center text-slate-600">
                                      <Music className="w-4 h-4 mr-2 text-violet-500" />
                                      {event.club_name}
                                    </div>
                                  ) : event.location && (
                                    <div className="flex items-center text-slate-600">
                                      <MapPin className="w-4 h-4 mr-2 text-violet-500" />
                                      <span className="line-clamp-1">{event.location}</span>
                                    </div>
                                  )}
                                  <div className="flex items-center text-slate-600">
                                    <Clock className="w-4 h-4 mr-2 text-teal-500" />
                                    {event.event_time}
                                  </div>
                                  {(() => {
                                    const currentTier = getCurrentTier(event.ticket_tiers);
                                    if (currentTier && !currentTier.notYetActive) {
                                      return (
                                        <div className="space-y-1">
                                          <div className="flex items-center text-slate-600">
                                            <PoundSterling className="w-4 h-4 mr-2 text-green-500" />
                                            <span className="font-semibold">£{currentTier.price}</span>
                                            <Badge variant="secondary" className="ml-2 text-xs bg-violet-100 text-violet-700">
                                              {currentTier.name}
                                            </Badge>
                                          </div>
                                        </div>
                                      );
                                    } else if (event.price) {
                                      return (
                                        <div className="flex items-center text-slate-600">
                                          <PoundSterling className="w-4 h-4 mr-2 text-green-500" />
                                          £{event.price}
                                        </div>
                                      );
                                    }
                                    return null;
                                  })()}
                                </div>
                              </div>
                            </Link>
                          </div>
                        </motion.div>
                      ))}
                  </div>
                  
                  {/* Divider between featured and regular events */}
                  {events.filter(e => !e.is_featured).length > 0 && (
                    <div className="mt-6 flex items-center gap-4">
                      <div className="flex-1 h-px bg-violet-200" />
                      <span className="text-sm text-violet-400 font-medium">More Events</span>
                      <div className="flex-1 h-px bg-violet-200" />
                    </div>
                  )}
                </div>
            )}
            
            {/* Regular Events Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {events.filter(e => !e.is_featured).map((event, index) => (
              <motion.div
                key={event.event_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <div className="bg-white rounded-2xl overflow-hidden border border-slate-100 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 h-full group">
                  <div className="relative h-48 overflow-hidden">
                    <Link to={getEventUrl(event)} data-testid={`event-card-${event.event_id}`}>
                      <img
                        src={getEventImage(event.event_id, event.image_url)}
                        alt={event.title}
                        className="w-full h-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
                      />
                      <div className="absolute top-3 left-3 bg-violet-500 text-white font-bold px-4 py-2 rounded-lg text-center">
                        <div className="text-2xl leading-none">{new Date(event.event_date).getDate()}</div>
                        <div className="text-xs uppercase">{new Date(event.event_date).toLocaleDateString('en-GB', { month: 'short' })}</div>
                      </div>
                      {event.type === 'community' && (
                        <Badge className="absolute top-3 right-3 bg-blue-500 text-white">
                          <Globe className="w-3 h-3 mr-1" />
                          Community
                        </Badge>
                      )}
                    </Link>
                    {/* External ticket widget code - takes priority */}
                    {event.ticket_widget_code && (
                      <Link
                        to={`${getEventUrl(event)}#tickets`}
                        onClick={(e) => e.stopPropagation()}
                        className="absolute bottom-0 left-0 right-0 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white px-4 py-2 flex items-center justify-center gap-2 cursor-pointer transition-all"
                        data-testid={`buy-tickets-widget-${event.event_id}`}
                      >
                        <Ticket className="w-4 h-4" />
                        <span className="text-sm font-semibold">Get Tickets</span>
                      </Link>
                    )}
                    {/* External ticket URL - second priority */}
                    {!event.ticket_widget_code && event.external_ticket_url && (
                      <a
                        href={event.external_ticket_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="absolute bottom-0 left-0 right-0 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white px-4 py-2 flex items-center justify-center gap-2 cursor-pointer transition-all"
                        data-testid={`buy-tickets-external-${event.event_id}`}
                      >
                        <Ticket className="w-4 h-4" />
                        <span className="text-sm font-semibold">Get Tickets</span>
                      </a>
                    )}
                    {/* Internal ticketing - only if no external options */}
                    {!event.ticket_widget_code && !event.external_ticket_url && event.ticketing_enabled && (
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setSelectedTicketEvent(event);
                          setTicketModalOpen(true);
                        }}
                        className="absolute bottom-0 left-0 right-0 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white px-4 py-2 flex items-center justify-center gap-2 cursor-pointer transition-all"
                        data-testid={`buy-tickets-${event.event_id}`}
                      >
                        <Ticket className="w-4 h-4" />
                        <span className="text-sm font-semibold">Buy Tickets Here</span>
                      </button>
                    )}
                  </div>
                  <Link to={getEventUrl(event)}>
                    <div className="p-5">
                      <h3 className="text-lg font-bold text-slate-900 mb-2 line-clamp-1 group-hover:text-violet-600 transition-colors">{event.title}</h3>
                      <p className="text-slate-600 text-sm line-clamp-2 mb-4">{event.description}</p>
                      
                      <div className="space-y-2 text-sm">
                        {event.type === 'club' ? (
                          <div className="flex items-center text-slate-600">
                            <Music className="w-4 h-4 mr-2 text-violet-500" />
                            {event.club_name}
                          </div>
                        ) : event.location && (
                          <div className="flex items-center text-slate-600">
                            <MapPin className="w-4 h-4 mr-2 text-violet-500" />
                            <span className="line-clamp-1">{event.location}</span>
                          </div>
                        )}
                        <div className="flex items-center text-slate-600">
                          <Clock className="w-4 h-4 mr-2 text-teal-500" />
                          {event.event_time}
                        </div>
                        {/* Price display with ticket tiers */}
                        {(() => {
                          const currentTier = getCurrentTier(event.ticket_tiers);
                          if (currentTier && !currentTier.notYetActive) {
                            return (
                              <div className="space-y-1">
                                <div className="flex items-center text-slate-600">
                                  <PoundSterling className="w-4 h-4 mr-2 text-green-500" />
                                  <span className="font-semibold">£{currentTier.price}</span>
                                  <Badge variant="secondary" className="ml-2 text-xs bg-violet-100 text-violet-700">
                                    {currentTier.name}
                                  </Badge>
                                </div>
                                {currentTier.daysRemaining && currentTier.daysRemaining <= 14 && (
                                  <div className={`text-xs ${currentTier.isExpiringSoon ? 'text-orange-600 font-medium' : 'text-slate-500'}`}>
                                    <Tag className="w-3 h-3 inline mr-1" />
                                    {currentTier.isExpiringSoon 
                                      ? `Only ${currentTier.daysRemaining} day${currentTier.daysRemaining === 1 ? '' : 's'} left at this price!`
                                      : `${currentTier.daysRemaining} days left at this price`
                                    }
                                  </div>
                                )}
                              </div>
                            );
                          } else if (event.price) {
                            return (
                              <div className="flex items-center text-slate-600">
                                <PoundSterling className="w-4 h-4 mr-2 text-green-500" />
                                £{event.price}
                              </div>
                            );
                          }
                          return null;
                        })()}
                        {event.contact && (
                          <div className="text-xs text-slate-500 mt-2 pt-2 border-t border-slate-100">
                            Contact: {event.contact}
                          </div>
                        )}
                      </div>
                    </div>
                  </Link>
                </div>
              </motion.div>
            ))}
            </div>
          </>
        )}
      </div>
      
      {/* Ticket Purchase Modal */}
      <TicketPurchaseModal
        isOpen={ticketModalOpen}
        onClose={() => {
          setTicketModalOpen(false);
          setSelectedTicketEvent(null);
        }}
        eventId={selectedTicketEvent?.event_id}
        eventTitle={selectedTicketEvent?.title}
      />
    </div>
  );
}
